import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, CheckCircle2, XCircle, Users, BadgeCheck, HeartPulse } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { inr, apiError, catLabel, stars } from "@/lib/format";
import { Button } from "@/components/ui/button";
import EmiChart from "@/components/EmiChart";
import LoanRequestModal from "@/components/LoanRequestModal";

function VoteCard({ r, onVote }) {
  const cat = catLabel(r.requester_category);
  const closed = r.status !== "voting";
  const pct = Math.round((r.approvals_count / Math.max(1, r.total_members)) * 100);
  const needPct = Math.round((r.needed / Math.max(1, r.total_members)) * 100);
  const extra = Math.max(0, r.approvals_count - (r.approver_names?.length || 0));
  const statusBadge = {
    voting: { t: "मतदान जारी / Voting", cls: "bg-amber-100 text-amber-700" },
    approved: { t: "स्वीकृत / Approved", cls: "bg-emerald-100 text-emerald-700" },
    rejected: { t: "अस्वीकृत / Rejected", cls: "bg-red-100 text-red-700" },
  }[r.status];

  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm border border-slate-100" data-testid={`loan-request-${r.id}`}>
      {/* Header */}
      <div className="rounded-xl bg-rose-50 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar name={r.member_name} size={44} />
            <div>
              <p className="font-display font-bold">{r.member_name} {r.is_mine && <span className="text-xs text-primary">(आप)</span>}</p>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cat.cls}`}>
                <BadgeCheck className="h-3 w-3" /> {cat.hi} सदस्य / {cat.en} Member
              </span>
            </div>
          </div>
          <span className={`px-2 py-1 rounded-full text-[10px] font-semibold ${statusBadge.cls}`}>{statusBadge.t}</span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2 font-mono">सदस्य ID: {r.member_code} • अवधि {r.tenure_days} दिन</p>
      </div>

      {/* Loan details */}
      <div className="grid grid-cols-2 gap-3 mt-3 rounded-xl bg-blue-50 p-3">
        <div>
          <p className="text-[11px] text-muted-foreground">ऋण राशि / Amount</p>
          <p className="text-2xl font-mono font-bold text-primary">{inr(r.amount)}</p>
        </div>
        <div className="text-right">
          <p className="text-[11px] text-muted-foreground">अवधि / Tenure</p>
          <p className="text-2xl font-mono font-bold">{r.tenure_days}<span className="text-sm"> दिन</span></p>
        </div>
        <div className="col-span-2 pt-2 border-t border-blue-100">
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <HeartPulse className="h-3.5 w-3.5 text-rose-500" /> उद्देश्य / Purpose: <span className="text-foreground font-medium">{r.purpose}</span>
          </p>
          <p className="text-xs text-muted-foreground mt-1">ब्याज दर / Interest: {r.interest_rate}% p.a. वार्षिक</p>
        </div>
      </div>

      {/* Repayment */}
      <div className="mt-3 rounded-xl border border-slate-100 p-3">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">मासिक EMI / Monthly</span>
          <span className="font-mono font-bold">{inr(r.emi)}</span>
        </div>
        <div className="flex justify-between text-sm mt-1">
          <span className="text-muted-foreground">कुल भुगतान / Total</span>
          <span className="font-mono font-semibold">{inr(r.total_repayment)}</span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1 font-mono">
          गणना: ₹{r.amount} × {r.interest_rate}% × {r.tenure_days}/365 = ₹{r.interest} ब्याज
        </p>
      </div>

      {/* Repayment history */}
      <div className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 p-2.5 text-xs">
        <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
        <span className="text-emerald-800">ऋण इतिहास: क्रेडिट स्कोर <span className="text-amber-500">{stars(r.requester_credit)}</span> • अच्छा / Good history</span>
      </div>

      {/* Voting */}
      <div className="mt-3">
        <div className="flex justify-between text-xs mb-1.5">
          <span className="flex items-center gap-1 text-muted-foreground"><Users className="h-3 w-3" /> अनुमोदन / Approvals {r.approvals_count}/{r.total_members}</span>
          <span className="text-muted-foreground">{pct}% • न्यूनतम {needPct}% चाहिए</span>
        </div>
        {r.approver_names?.length > 0 && (
          <div className="flex items-center gap-1 mb-2">
            {r.approver_names.map((n, i) => (
              <div key={i} className="relative" style={{ marginLeft: i === 0 ? 0 : -8 }}>
                <Avatar name={n} size={26} className="ring-2 ring-white" />
              </div>
            ))}
            <span className="text-[11px] text-muted-foreground ml-1.5">
              {r.approver_names.slice(0, 3).map((n) => n.split(" ")[0]).join(", ")}{extra > 0 ? ` +${extra} ने हाँ कहा` : " ने हाँ कहा"}
            </span>
          </div>
        )}
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden relative">
          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
          <div className="absolute top-0 bottom-0 w-0.5 bg-slate-400" style={{ left: `${needPct}%` }} />
        </div>
      </div>

      {/* Terms */}
      <ul className="mt-3 space-y-1 text-[11px] text-muted-foreground">
        <li>• समय पर भुगतान न करने पर 2% विलंब शुल्क लगेगा</li>
        <li>• ऋण {r.tenure_days} दिन में चुकाना आवश्यक</li>
        <li>• समूह नियमों के अनुसार ब्याज वितरण होगा</li>
      </ul>

      {!closed && !r.is_mine && (
        <div className="grid grid-cols-2 gap-2 mt-3">
          <Button variant="outline" data-testid={`vote-reject-${r.id}`} disabled={r.my_vote === "reject"} onClick={() => onVote(r.id, "reject")} className="border-red-200 text-red-600 hover:bg-red-50">
            <XCircle className="h-4 w-4 mr-1" /> अस्वीकार
          </Button>
          <Button data-testid={`vote-approve-${r.id}`} disabled={r.my_vote === "approve"} onClick={() => onVote(r.id, "approve")}>
            <CheckCircle2 className="h-4 w-4 mr-1" /> अनुमोदन
          </Button>
        </div>
      )}
      {r.my_vote && !closed && (
        <p className="text-xs text-center mt-2 text-primary font-medium" data-testid={`my-vote-${r.id}`}>
          आपने {r.my_vote === "approve" ? "अनुमोदन" : "अस्वीकार"} किया ✓
        </p>
      )}
    </div>
  );
}

export default function LoansScreen() {
  const { member, refresh } = useAuth();
  const isAdmin = member.role === "admin";
  const [loan, setLoan] = useState(null);
  const [requests, setRequests] = useState([]);
  const [paying, setPaying] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);

  const load = async () => {
    const calls = [api.get("/loan-requests")];
    if (!isAdmin) calls.push(api.get("/loan/me"));
    const res = await Promise.all(calls);
    setRequests(res[0].data);
    if (!isAdmin) setLoan(res[1].data);
  };
  useEffect(() => { load(); }, []);

  const payEmi = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/loan/pay-emi");
      toast.success(`EMI भुगतान हुआ — ₹${data.paid}`);
      await refresh();
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setPaying(false); }
  };

  const vote = async (id, v) => {
    try {
      const { data } = await api.post(`/loan-requests/${id}/vote`, { vote: v });
      toast.success(data.status === "approved" ? "ऋण स्वीकृत! 🎉" : data.status === "rejected" ? "ऋण अस्वीकृत" : "मतदान दर्ज हुआ");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      {!isAdmin && (
        <>
          <EmiChart loan={loan} onPay={payEmi} paying={paying} />
          <Button data-testid="open-loan-request-btn" onClick={() => setReqOpen(true)} className="w-full h-12 text-base">
            <Plus className="h-5 w-5 mr-1" /> नया ऋण अनुरोध / Request Loan
          </Button>
        </>
      )}

      <div>
        <h3 className="font-display font-bold text-lg mb-2">ऋण अनुरोध / Loan Requests</h3>
        <div className="space-y-3">
          {requests.length === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm">कोई ऋण अनुरोध नहीं</p>
          ) : (
            requests.map((r) => <VoteCard key={r.id} r={r} onVote={vote} />)
          )}
        </div>
      </div>

      <LoanRequestModal open={reqOpen} onOpenChange={setReqOpen} onDone={load} />
    </div>
  );
}
